
import { Vue, Component, Prop } from 'vue-property-decorator'
import * as T from '@/types'
import { required, email, sameAs, minLength, alphaNum, not } from 'vuelidate/lib/validators'
import api from '@/api'
import S from '@/store'
import config from '@/config'

import AppWordmark from '@/components/app/AppWordmark.vue'

import {
    UiBox,
    UiButton,
    UiCard,
    UiFormattedText,
    UiModal,
    UiTextInput,
} from '@hubsolv/hubsolv-ui'

export enum ModalKey {
    NONE = 'none',
    SUCCESS = 'success',
    INVALID_TOKEN = 'invalidToken',
    WEAK_PASSWORD = 'weakPassword',
}

export interface ModalConfig {
    title: string
    message: any[]
    info?: any[]
    onConfirm: () => void
}

@Component({
    components: {
        AppWordmark,
        UiBox,
        UiButton,
        UiCard,
        UiFormattedText,
        UiModal,
        UiTextInput,
    },
    validations: {
        password: {
            required,
            minLength: (value: string) => value && value.length >= 8,
            containsSymbol : not(alphaNum),
            containsNumber : (value: string) => /\d/.test(value),
            containsUpper  : (value: string) => value.toLowerCase() !== value,
            containsLower  : (value: string) => value.toUpperCase() !== value,
        },
        passwordConfirmation: {
            required,
            sameAsPassword: sameAs('password'),
        },
    },
})
export default class ViewPasswordReset extends Vue {

    ////////////////////////////////////////////////////////////////
    // first login 
    ////////////////////////////////////////////////////////////////
    
    /**
     * True if the query contains `firstLogin=true`
     */
    get firstLogin () {
        return this.$route.query.firstLogin === 'true'
    }

    ////////////////////////////////////////////////////////////////
    // token validation
    ////////////////////////////////////////////////////////////////
    
    /**
     * The token contained in the query string
     */
    get token () {
        return this.$route.query.token
    }

    /**
     * Redirects to login screen if no token is provided
     */
    created () {
        if(!this.token){
            console.log('No token - redirecting to login.')
            this.$router.replace({name: T.CrmRoute.AUTH_LOGIN })
            return
        }
    }

    ////////////////////////////////////////////////////////////////
    // form
    ////////////////////////////////////////////////////////////////

    @S.Auth.Action login: any
    @S.Auth.Action logout: any

    loading: boolean = false

    password: string = ''
    passwordConfirmation: string = ''
    
    /**
     * Requirements for the password to be valid.
     */
    get passwordRequirements () {
        if (!this.$v) return null
        return [
            {
                satisfied: this.$v.password.minLength ,
                message: 'Must be at least 8 characters long.',
            },
            {
                satisfied: this.$v.password.containsUpper ,
                message: 'Must contain at least one uppercase character.',
            },
            {
                satisfied: this.$v.password.containsLower ,
                message: 'Must contain at least one lowercase character.',
            },
            {
                satisfied: this.$v.password.containsNumber ,
                message: 'Must contain at least one number.',
            },
            {
                satisfied: this.password !== '' && this.$v.password.containsSymbol ,
                message: 'Must contain at least one symbol.',
            },
        ]
    }
    
    /**
     * Form errors.
     */
    get formErrors () {
        if (!this.$v) return null
        if (!this.$v.$error) return null

        const errors: any = {}
        if (this.$v.password.$error) errors.password = ['Please make sure all password requirements are satisfied.']
        if (!this.$v.passwordConfirmation.sameAsPassword) errors.confirmation = ['Please make sure your passwords match.']

        return errors
    }

    /**
     * Submit form
     */
    async onSubmit () {
        if (!this.$v) return
        this.$v.$touch()
        if (this.$v.$invalid) return

        try {
            this.loading = true
            if (this.firstLogin) {
                await api.auth.setPassword(this.token, this.password, this.passwordConfirmation)
            } else {
                await api.auth.resetPassword(this.token, this.password, this.passwordConfirmation)
            }
            this.modalKey = ModalKey.SUCCESS
        } catch ({response}) {
            console.error('Error resetting password:', response)
            if (response.status === 422) {
                this.modalKey = ModalKey.WEAK_PASSWORD
            } else {
                this.modalKey = ModalKey.INVALID_TOKEN
            }
        } finally {
            this.loading = false
        }
    }

    ////////////////////////////////////////////////////////////////
    // ui → mobile
    ////////////////////////////////////////////////////////////////

    get mobileClass () {
        return {'mobile': this.$mq === 'sm'}
    }

    ////////////////////////////////////////////////////////////////
    // modal
    ////////////////////////////////////////////////////////////////
    
    modalKey: ModalKey = ModalKey.NONE

    get modalConfig (): Record<ModalKey, ModalConfig | {}> {
        return {
            [ModalKey.SUCCESS]: {
                title: 'Password changed successfully',
                message: [
                    { text: 'You will now be directed to the login screen.'},
                    { text: 'Please log in with your new credentials.'},
                 ],
                onConfirm: () => {
                    this.modalKey = ModalKey.NONE
                    if (this.firstLogin) {
                        this.logout()
                    } else {
                        this.$router.replace({ name: T.CrmRoute.AUTH_LOGIN })
                    }
                },
            },
            [ModalKey.INVALID_TOKEN]: {
                title: 'Password change failed',
                message: [
                    { text: 'Your password reset token was invalid.'}, 
                    { text: 'Please request a new password reset email.', break: true},
                ],
                info: [
                    { text: `For support, contact us at`, subtle: false},
                    { text: `${this.support.email}`, subtle: true, link: { href: `mailto:${this.support.email}`, target: '_blank' } },
                    { text: `.` },
                ],
                onConfirm: () => {
                    this.modalKey = ModalKey.NONE
                    this.$router.replace({ name: T.CrmRoute.AUTH_LOGIN })
                },
            },
            [ModalKey.WEAK_PASSWORD]: {
                title: 'Weak password',
                message: [
                    { text: 'This password has been identified as a weak or commonly used password.'}, 
                    { text: 'Please try to use a strong password that is unique to your account.'}, 
                ],
                info: [
                    { text: `Recommendation provided by` },
                    { text: 'haveibeenpwned.com', link: { href: `https://haveibeenpwned.com`, target: `_blank` } }, 
                ],
                onConfirm: () => {
                    this.modalKey = ModalKey.NONE
                },
            },
            [ModalKey.NONE]: {},
        }
    }

    get support () {
        return {
            email: config.SUPPORT.SUPPORT_EMAIL_ADDRESS,
        }
    }

}
